import { useCallback, useEffect, useRef, useState } from "react";
import { useBoardRepo } from "./context";
import { applyChange } from "./repo";
import type { BoardSnapshot } from "./types";

/**
 * One board, kept live. Loads through the repo, then applies Realtime
 * changes row by row instead of reloading on every event. A full reload
 * happens only after the socket reconnects, because anything sent while it
 * was down was missed.
 */
export function useBoard(boardId: string | undefined) {
  const repo = useBoardRepo();
  const [snap, setSnap] = useState<BoardSnapshot | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const live = useRef(true);

  const reload = useCallback(async () => {
    if (!boardId) return;
    try {
      const s = await repo.load(boardId);
      if (live.current) { setSnap(s); setError(null); }
    } catch (e) {
      if (live.current) setError((e as Error).message);
    } finally {
      if (live.current) setLoading(false);
    }
  }, [repo, boardId]);

  useEffect(() => {
    live.current = true;
    setLoading(true);
    void reload();
    if (!boardId) return;
    const stop = repo.subscribe(
      boardId,
      c => setSnap(s => (s ? applyChange(s, c) : s)),
      () => void reload(),
    );
    return () => { live.current = false; stop(); };
  }, [repo, boardId, reload]);

  return { snap, setSnap, error, setError, loading, reload, repo };
}
